import { Check, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface EditableNoteProps {
  content: string;
  onSave: (content: string) => Promise<void>;
  onDelete: () => Promise<void>;
  isSaving?: boolean;
  isDeleting?: boolean;
  className?: string;
}

/**
 * Note content with inline edit and delete affordances.
 * Shared by the customer, transaction, case, promise and dispute note lists so
 * editing behaves identically in every domain.
 */
export function EditableNote({
  content,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  className,
}: EditableNoteProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSavingInternal, setIsSavingInternal] = useState(false);
  const [isDeletingInternal, setIsDeletingInternal] = useState(false);

  // Callers that cannot scope their mutation state to a single note omit the flags,
  // so the pending state of the in-flight call is tracked here as well.
  const saving = isSaving || isSavingInternal;
  const deleting = isDeleting || isDeletingInternal;

  const startEditing = () => {
    setDraft(content);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(content);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === content) {
      cancelEditing();
      return;
    }

    setIsSavingInternal(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch {
      // Keep the editor open with the draft intact; the caller surfaces the failure
    } finally {
      setIsSavingInternal(false);
    }
  };

  const handleDelete = async () => {
    setIsDeletingInternal(true);
    try {
      await onDelete();
      setDeleteDialogOpen(false);
    } catch {
      // Keep the confirmation open; the caller surfaces the failure
    } finally {
      setIsDeletingInternal(false);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="min-h-[80px] resize-none text-sm"
          disabled={saving}
          autoFocus
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
            <X className="mr-1 h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !draft.trim()}>
            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start justify-between gap-2">
      <div className={className ?? 'text-sm leading-relaxed whitespace-pre-wrap break-words'}>{content}</div>
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={startEditing}
          disabled={deleting}
          aria-label="Edit note"
          title="Edit note"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={deleting}
          aria-label="Delete note"
          title="Delete note"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Radix closes the dialog on action click; keep it open until the delete resolves
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
